import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RELIGIONS, MARITAL, ageFromDob } from "@/components/wizard/validation";

export default function PersonForm({ person, setPerson, label, idPrefix, subtitle }) {
  const age = ageFromDob(person.date_of_birth);
  const set = (k) => (v) => setPerson({ ...person, [k]: v });

  return (
    <div data-testid={`person-form-${idPrefix}`}>
      <h2 className="font-serif text-2xl text-navy">{label}</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        {subtitle || "Basic personal information — as per your official records."}
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor={`${idPrefix}-name`}>Full legal name *</Label>
          <Input
            id={`${idPrefix}-name`}
            className="mt-2"
            value={person.full_name}
            onChange={(e) => set("full_name")(e.target.value)}
            placeholder="e.g. Priya Anand Sharma"
            data-testid={`${idPrefix}-name`}
          />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-dob`}>Date of Birth *</Label>
          <Input
            id={`${idPrefix}-dob`}
            type="date"
            className="mt-2"
            value={person.date_of_birth}
            onChange={(e) => set("date_of_birth")(e.target.value)}
            data-testid={`${idPrefix}-dob`}
          />
          {age !== null && (
            <p className={`mt-1 text-xs ${age < 18 ? "text-red-600" : "text-[#4A5568]"}`}>
              Age: {age} {age < 18 && "— must be 18 or older to create a will."}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-religion`}>Religion *</Label>
          <Select value={person.religion} onValueChange={(v) => set("religion")(v)}>
            <SelectTrigger className="mt-2" id={`${idPrefix}-religion`} data-testid={`${idPrefix}-religion`}>
              <SelectValue placeholder="Select religion" />
            </SelectTrigger>
            <SelectContent>
              {RELIGIONS.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-marital`}>Marital Status *</Label>
          <Select value={person.marital_status} onValueChange={(v) => set("marital_status")(v)}>
            <SelectTrigger className="mt-2" id={`${idPrefix}-marital`} data-testid={`${idPrefix}-marital`}>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {MARITAL.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${idPrefix}-address`}>Residential address *</Label>
          <Textarea
            id={`${idPrefix}-address`}
            rows={3}
            className="mt-2"
            value={person.address}
            onChange={(e) => set("address")(e.target.value)}
            placeholder="Flat / Building / Street / City / Pincode"
            data-testid={`${idPrefix}-address`}
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`${idPrefix}-city`}>City of signing</Label>
          <Input
            id={`${idPrefix}-city`}
            className="mt-2"
            value={person.city || ""}
            onChange={(e) => set("city")(e.target.value)}
            placeholder="e.g. Mumbai"
            data-testid={`${idPrefix}-city`}
          />
          <p className="mt-1 text-xs text-[#4A5568]">Appears as "Signed at" in your Will.</p>
        </div>
        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gold/40 bg-[#FBF7EE] p-4">
            <Checkbox
              checked={!!person.sound_mind}
              onCheckedChange={(v) => set("sound_mind")(!!v)}
              className="mt-0.5 border-navy data-[state=checked]:bg-navy data-[state=checked]:text-gold"
              data-testid={`${idPrefix}-sound-mind`}
            />
            <span className="text-sm text-navy">
              I declare that I am of sound mind and I am making this Will voluntarily, without any coercion or undue influence.
            </span>
          </label>
        </div>

        {person.religion === "Muslim" && (
          <div
            className="sm:col-span-2 rounded-md border border-gold/50 bg-[#FBF7EE] p-5"
            data-testid={`${idPrefix}-muslim-notice`}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Specialised handling required
            </div>
            <p className="mt-2 text-sm text-navy">
              Muslim inheritance laws (Wasiyat) involve specialised rules — including the
              one-third limit on bequests to non-heirs, and rights under Muslim Personal Law.
              Our free template engine does not cover these correctly.
            </p>
            <p className="mt-2 text-sm text-[#4A5568]">
              Please request a <span className="font-medium text-navy">Lawyer Review</span> and our
              senior team will personally draft your Will under Muslim Personal Law.
            </p>
            <a
              href="https://wa.me/919167249612?text=Hi%20WillSeva%2C%20I%20would%20like%20a%20Lawyer%20Review%20for%20a%20Will%20under%20Muslim%20Personal%20Law.%20Please%20guide%20me%20on%20next%20steps.%20Thank%20you."
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-[#1A2642]"
              data-testid={`${idPrefix}-muslim-lawyer-cta`}
            >
              Request Lawyer Review on WhatsApp
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
